import React from "react";
import { InsightCircle } from "../../shared-components/InsightCircle";
import ReactTooltip from "react-tooltip";
import { DonutChart } from "../../shared-components/DonutChart";
import { computeFlagsCountTotal, FlagsCountPayload } from "../../api/Reports";
import { INSIGHT_COLORS } from "../utils";
import { ReportRowItem } from "./TableModels";
import { classNames } from "../../utils";

type Props<T extends { flags_count_payload?: FlagsCountPayload }> = {
  reportRow: ReportRowItem<T>;
  flagKey: string;
};

const ReportsCircleTooltip = <
  T extends { flags_count_payload?: FlagsCountPayload }
>({
  reportRow,
  flagKey,
}: Props<T>) => {
  const totalFlags = computeFlagsCountTotal(
    reportRow.report?.flags_count_payload
  );
  const totalArticles =
    reportRow.report?.flags_count_payload?.articles_count || 0;
  let chartData = [{ label: "Default", value: 1, color: "#CAD0D3" }];
  if (totalFlags > 0) {
    chartData = [
      {
        label: "Community Count",
        value: reportRow.report?.flags_count_payload?.community_count || 0,
        color: INSIGHT_COLORS.community,
      },
      {
        label: "Adverse Flag Count",
        value: reportRow.report?.flags_count_payload?.adverse_flag_count || 0,
        color: INSIGHT_COLORS.adverse,
      },
      {
        label: "Articles Count",
        value: totalArticles,
        color: INSIGHT_COLORS.articles,
      },
      {
        label: "Diligence",
        value: reportRow.report?.flags_count_payload?.diligence || 0,
        color: INSIGHT_COLORS.diligence,
      },
      {
        label: "Keywords Count",
        value: reportRow.report?.flags_count_payload?.keywords || 0,
        color: INSIGHT_COLORS.keywords,
      },
      {
        label: "Topics Count",
        value: reportRow.report?.flags_count_payload?.topics || 0,
        color: INSIGHT_COLORS.topics,
      },
      {
        label: "News & Web",
        value: reportRow.report?.flags_count_payload?.news || 0,
        color: INSIGHT_COLORS.news,
      },
      {
        label: "Legal Cases",
        value: reportRow.report?.flags_count_payload?.legal || 0,
        color: INSIGHT_COLORS.legal,
      },
    ];
  }
  return (
    <>
      <InsightCircle
        flagkey={flagKey}
        tooltip={"flags-count-" + flagKey}
        data={chartData}
        totalFlags={totalFlags}
      />
      <ReactTooltip
        className={"drop-shadow-2xl"}
        backgroundColor={"#FFFFFF"}
        id={"flags-count-" + flagKey}
      >
        <div className={"flex justify-between w-84 text-black"}>
          <div className={"w-36"}>
            <DonutChart data={chartData} cutout={55} enableTooltip={false}>
              <span className={"absolute top-[30%] left-[21%] text-center"}>
                <p className={"text-xl"}>{totalFlags}</p>
                Total
                <br />
                Items
              </span>
            </DonutChart>
          </div>
          {!totalFlags && (
            <>
              <div className={"flex flex-row items-center px-10"}>
                <div>
                  <span>No Items found</span>
                </div>
              </div>
            </>
          )}
          {totalFlags > 0 && (
            <div
              className={
                "border-l border-gray-300 pl-4 flex flex-col justify-center gap-2 ml-4 w-36"
              }
            >
              {!!reportRow.report?.flags_count_payload?.community_count && (
                <div className="flex flex-row items-center">
                  <div
                    className={classNames(
                      "w-2 h-2 rounded-full mr-2 aspect-ratio-1/1 scale-1.2"
                    )}
                    style={{ backgroundColor: INSIGHT_COLORS.community }}
                    aria-label="Community Count Color"
                  />
                  <div>
                    <span>Community: </span>
                    <span className="font-bold">
                      {reportRow.report?.flags_count_payload?.community_count}
                    </span>
                  </div>
                </div>
              )}
              {!!reportRow.report?.flags_count_payload?.adverse_flag_count && (
                <div className={"flex flex-row items-center"}>
                  <div
                    className={classNames(
                      "w-2 h-2 rounded-full mr-2 aspect-ratio-1/1 scale-1.2"
                    )}
                    style={{ backgroundColor: INSIGHT_COLORS.adverse }}
                    aria-label="Adverse Flag Count Color"
                  />
                  <div>
                    <span>Adverse Flag: </span>
                    <span className={"font-bold"}>
                      {reportRow.report?.flags_count_payload
                        ?.adverse_flag_count || 0}
                    </span>
                  </div>
                </div>
              )}
              {!!totalArticles && (
                <div className={"flex flex-row items-center"}>
                  <div
                    className={classNames(
                      "w-2 h-2 rounded-full mr-2 aspect-ratio-1/1 scale-1.2"
                    )}
                    style={{ backgroundColor: INSIGHT_COLORS.articles }}
                    aria-label="Articles Count Color"
                  />
                  <div>
                    <span>Articles: </span>
                    <span className={"font-bold"}>{totalArticles}</span>
                  </div>
                </div>
              )}
              {!!reportRow.report?.flags_count_payload?.diligence && (
                <div className={"flex flex-row items-center"}>
                  <div
                    className={classNames(
                      "w-2 h-2 rounded-full mr-2 aspect-ratio-1/1 scale-1.2"
                    )}
                    style={{ backgroundColor: INSIGHT_COLORS.diligence }}
                  />
                  <div>
                    <span>Diligence: </span>
                    <span className={"font-bold"}>
                      {reportRow.report?.flags_count_payload?.diligence || 0}
                    </span>
                  </div>
                </div>
              )}
              {!!reportRow.report?.flags_count_payload?.keywords && (
                <div className={"flex flex-row items-center"}>
                  <div
                    className={classNames(
                      "w-2 h-2 rounded-full mr-2 aspect-ratio-1/1 scale-1.2"
                    )}
                    style={{ backgroundColor: INSIGHT_COLORS.keywords }}
                    aria-label="Keywords Count Color"
                  />
                  <div>
                    <span>Keywords: </span>
                    <span className={"font-bold"}>
                      {reportRow.report?.flags_count_payload?.keywords || 0}
                    </span>
                  </div>
                </div>
              )}
              {!!reportRow.report?.flags_count_payload?.topics && (
                <div className={"flex flex-row items-center"}>
                  <div
                    className={classNames(
                      "w-2 h-2 rounded-full mr-2 aspect-ratio-1/1 scale-1.2"
                    )}
                    style={{ backgroundColor: INSIGHT_COLORS.topics }}
                    aria-label="Topics Count Color"
                  />
                  <div>
                    <span>Topics: </span>
                    <span className={"font-bold"}>
                      {reportRow.report?.flags_count_payload?.topics || 0}
                    </span>
                  </div>
                </div>
              )}
              {!!reportRow.report?.flags_count_payload?.news && (
                <div className={"flex flex-row items-center"}>
                  <div
                    className={classNames(
                      "w-2 h-2 rounded-full mr-2 aspect-ratio-1/1 scale-1.2"
                    )}
                    style={{ backgroundColor: INSIGHT_COLORS.news }}
                    aria-label="News Count Color"
                  />
                  <div>
                    <span>News & Web: </span>
                    <span className={"font-bold"}>
                      {reportRow.report?.flags_count_payload?.news || 0}
                    </span>
                  </div>
                </div>
              )}
              {!!reportRow.report?.flags_count_payload?.legal && (
                <div className={"flex flex-row items-center"}>
                  <div
                    className={classNames(
                      "w-2 h-2 rounded-full mr-2 aspect-ratio-1/1 scale-1.2"
                    )}
                    style={{ backgroundColor: INSIGHT_COLORS.legal }}
                    aria-label="Legal Cases Count Color"
                  />
                  <div>
                    <span>Legal Cases: </span>
                    <span className={"font-bold"}>
                      {reportRow.report?.flags_count_payload?.legal || 0}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </ReactTooltip>
    </>
  );
};

export default ReportsCircleTooltip;
